import { useEffect, useState } from "react";
import axiosAdmin from "../axiosAdmin";
import { ConfirmModal } from "../components/ConfirmModal";
import { StatusBadge } from "../components/StatusBadge";
import { 
  Users, 
  Trash2, 
  History, 
  Shield, 
  ShieldOff, 
  AlertTriangle, 
  ChevronLeft, 
  ChevronRight, 
  X,
  Search,
  Gift 
} from "lucide-react";

const LIMIT = 10;

const getOrderStatusClasses = (status) => {
    switch (status) {
        case 'ANNULEE':
            return 'bg-red-50 text-red-700 border-red-100';
        case 'LIVREE':
            return 'bg-green-50 text-green-700 border-green-100';
        case 'PRETE':
            return 'bg-blue-50 text-blue-700 border-blue-100';
        case 'EN PREPARATION':
            return 'bg-yellow-50 text-yellow-700 border-yellow-100';
        case 'EN ATTENTE':
            return 'bg-gray-100 text-gray-600 border-gray-200';
        default:
            return 'bg-gray-100 text-gray-600 border-gray-200';
    }
};

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [selectedUser, setSelectedUser] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [ordersOpen, setOrdersOpen] = useState(false);
  const [userOrders, setUserOrders] = useState([]);
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  const [offerFilter, setOfferFilter] = useState(false);
  
  // 🆕 ÉTATS POUR LA RECHERCHE
  const [searchTerm, setSearchTerm] = useState("");
  const [activeSearch, setActiveSearch] = useState(""); // Stocke la recherche validée

  const fetchUsers = async () => {
    try {
      setLoading(true);
      
      let url = `/admin/users?page=${page}&limit=${LIMIT}`;
      if (offerFilter) url += "&offerEligible=true";
      
      // 🆕 Ajout du paramètre de recherche à l'API
      if (activeSearch) url += `&search=${activeSearch}`;

      const res = await axiosAdmin.get(url);
      setUsers(res.data.users || []);
      setTotalPages(res.data.pagination?.pages || 1);
    } catch {
      setError("Impossible de charger les utilisateurs");
    } finally {
      setLoading(false);
    }
  };

  // 🔄 Écoute les changements de page, de filtre OU de recherche validée
  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, offerFilter, activeSearch]);

  // 🆕 FONCTIONS DE RECHERCHE
  const handleSearchSubmit = (e) => {
    e.preventDefault(); // Empêche le rechargement de la page
    setActiveSearch(searchTerm); // Valide la recherche
    setPage(1); // Retour à la page 1
  };

  const clearSearch = () => {
    setSearchTerm("");
    setActiveSearch("");
    setPage(1);
  };

  const handleToggleBlock = async () => {
    if (!selectedUser) return;
    await axiosAdmin.patch(`/admin/users/${selectedUser._id}/block`);
    setConfirmOpen(false);
    setSelectedUser(null);
    fetchUsers();
  };

  const openHistory = async (user) => {
    setSelectedUser(user);
    setOrdersOpen(true);
    const res = await axiosAdmin.get(`/admin/users/${user._id}/orders`);
    setUserOrders(res.data.orders || []);
  };

  const handleDeleteAllUsers = async () => {
    await axiosAdmin.delete("/admin/users");
    setConfirmDeleteAll(false);
    setPage(1);
    fetchUsers();
  };

  if (loading && users.length === 0) return (
    <div className="flex items-center justify-center min-h-[50vh] text-gray-400">
        Chargement des utilisateurs...
    </div>
  );
  
  if (error) return (
    <div className="p-8 m-8 text-center text-red-500 border border-red-100 bg-red-50 rounded-xl">
        {error}
    </div>
  );

  return (
    <div className="min-h-screen p-8 space-y-6 font-sans bg-gray-50/50">
      
      {/* HEADER : Titres et Boutons */}
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
        <div>
            <h1 className="flex items-center text-3xl font-extrabold tracking-tight text-gray-900">
                <Users className="mr-3 text-gray-400" size={28} />
                Utilisateurs
            </h1>
            <p className="mt-1 text-sm text-gray-500">Gestion des comptes clients et administrateurs</p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => {
              setOfferFilter(!offerFilter);
              setPage(1);
            }}
            className={`flex items-center px-4 py-2.5 text-sm font-bold rounded-xl border transition-all ${
              offerFilter
                ? "bg-emerald-50 text-emerald-700 border-emerald-200 shadow-sm ring-2 ring-emerald-500/20"
                : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50 shadow-sm"
            }`}
          >
            <Gift size={16} className={`mr-2 ${offerFilter ? "text-emerald-600 animate-pulse" : "text-gray-400"}`} />
            {offerFilter ? "Filtre : > 9 commandes" : "Clients à +9 commandes"}
          </button>

          <button
            onClick={() => setConfirmDeleteAll(true)}
            className="flex items-center px-4 py-2.5 text-sm font-bold text-red-600 bg-white border border-red-200 rounded-xl shadow-sm hover:bg-red-50 transition-colors"
          >
            <Trash2 size={16} className="mr-2" />
            Supprimer tous les utilisateurs
          </button>
        </div>
      </div>

      {/* 🆕 BARRE DE RECHERCHE */}
      <form onSubmit={handleSearchSubmit} className="relative w-full max-w-md">
        <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
          <Search size={18} className="text-gray-400" />
        </div>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Rechercher par nom ou numéro..."
          className="w-full py-3 pl-11 pr-24 text-sm bg-white border border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all"
        />
        <div className="absolute inset-y-0 right-1 flex items-center">
          {activeSearch && (
            <button
              type="button"
              onClick={clearSearch}
              className="p-2 mr-1 text-gray-400 hover:text-red-500 transition-colors"
              title="Effacer la recherche"
            >
              <X size={16} />
            </button>
          )}
          <button
            type="submit"
            className="px-4 py-1.5 mr-1 text-xs font-bold text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors"
          >
            Chercher
          </button>
        </div>
      </form>

      {/* TABLEAU DES UTILISATEURS */}
      <div className="overflow-hidden bg-white border border-gray-200 shadow-sm rounded-2xl">
        <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
            <thead className="text-xs font-semibold tracking-wider text-gray-500 uppercase border-b border-gray-100 bg-gray-50/80">
                <tr>
                <th className="px-6 py-4">Utilisateur</th>
                <th className="px-6 py-4">Contact</th>
                <th className="px-6 py-4">Rôle</th>
                <th className="px-6 py-4 text-center">Commandes</th>
                <th className="px-6 py-4">Total Dépensé</th>
                <th className="px-6 py-4">Statut</th>
                <th className="px-6 py-4">Risque</th>
                <th className="px-6 py-4 text-right">Actions</th>
                </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
                {users.map((u) => (
                <tr key={u._id} className={`hover:bg-gray-50 transition-colors ${u.isRisky ? "bg-red-50/30" : ""}`}>
                    <td className="px-6 py-4 font-medium text-gray-900">
                        <div className="flex items-center">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold mr-3 ${u.isRisky ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'}`}>
                                {u.firstName?.charAt(0)}{u.lastName?.charAt(0)}
                            </div>
                            {u.firstName} {u.lastName}
                        </div>
                    </td>
                    
                    <td className="px-6 py-4 font-mono text-xs text-gray-500">
                        {u.phone}
                    </td>
                    
                    <td className="px-6 py-4">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${u.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}>
                            {u.role}
                        </span>
                    </td>
                    
                    <td className="px-6 py-4 font-bold text-center text-gray-700">
                        {u.ordersCount}
                    </td>
                    
                    <td className="px-6 py-4 font-bold text-gray-900">
                        {u.totalSpent} <span className="text-xs font-normal text-gray-400">MRU</span>
                    </td>
                    
                    <td className="px-6 py-4">
                        <StatusBadge
                            active={!u.isBlocked}
                            activeLabel="Actif"
                            inactiveLabel="Bloqué"
                        />
                    </td>
                    
                    <td className="px-6 py-4">
                        {u.isRisky ? (
                            <span className="inline-flex items-center px-2.5 py-1 text-xs font-bold text-red-700 bg-red-100 border border-red-200 rounded-lg">
                                <AlertTriangle size={12} className="mr-1" />
                                À risque
                            </span>
                        ) : (
                            <span className="text-xs text-gray-300">—</span>
                        )}
                    </td>
                    
                    <td className="px-6 py-4 space-x-2 text-right">
                        <button
                            onClick={() => openHistory(u)}
                            className="inline-flex items-center px-3 py-1.5 text-xs font-bold text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:bg-white hover:shadow-sm transition-all"
                            title="Voir l'historique"
                        >
                            <History size={14} className="mr-1.5" />
                            Historique
                        </button>
                        
                        <button
                            onClick={() => {
                                setSelectedUser(u);
                                setConfirmOpen(true);
                            }}
                            className={`inline-flex items-center px-3 py-1.5 text-xs font-bold border rounded-lg transition-all ${
                                u.isBlocked
                                ? "bg-green-50 text-green-700 border-green-200 hover:bg-green-100"
                                : "bg-white text-red-600 border-red-200 hover:bg-red-50"
                            }`}
                        >
                            {u.isBlocked ? <Shield size={14} className="mr-1.5"/> : <ShieldOff size={14} className="mr-1.5"/>}
                            {u.isBlocked ? "Débloquer" : "Bloquer"}
                        </button>
                    </td>
                </tr>
                ))}

                {users.length === 0 && (
                <tr>
                    <td colSpan="8" className="px-6 py-12 text-center text-gray-400">
                        <div className="flex flex-col items-center">
                            <Search size={32} className="mb-2 opacity-20" />
                            Aucun utilisateur trouvé {activeSearch && `pour "${activeSearch}"`}
                        </div>
                    </td>
                </tr>
                )}
            </tbody>
            </table>
        </div>
      </div>

      {/* PAGINATION */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-6 border-t border-gray-200">
          <span className="text-sm font-medium text-gray-500">
            Page {page} sur {totalPages}
          </span>

          <div className="flex space-x-2">
            <button
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
              className="flex items-center px-4 py-2 text-sm font-bold text-gray-700 transition bg-white border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={16} className="mr-1" /> Précédent
            </button>
            <button
              disabled={page === totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="flex items-center px-4 py-2 text-sm font-bold text-gray-700 transition bg-white border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Suivant <ChevronRight size={16} className="ml-1" />
            </button>
          </div>
        </div>
      )}

      {/* MODALS */}
      {ordersOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm">
          <div className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 bg-gray-50">
                <div>
                    <h2 className="text-xl font-extrabold text-gray-800">Historique d'achat</h2>
                    <p className="text-sm text-gray-500">Client : <span className="font-semibold text-gray-700">{selectedUser?.firstName} {selectedUser?.lastName}</span></p>
                </div>
                <button onClick={() => setOrdersOpen(false)} className="p-2 text-gray-400 transition bg-white border border-gray-200 rounded-full hover:text-gray-600 hover:shadow-sm">
                    <X size={20} />
                </button>
            </div>

            <div className="p-0 overflow-y-auto">
                <table className="w-full text-sm text-left">
                <thead className="sticky top-0 text-gray-500 bg-white border-b border-gray-100">
                    <tr>
                    <th className="px-6 py-3 font-semibold bg-gray-50">Commande</th>
                    <th className="px-6 py-3 font-semibold bg-gray-50">Marchand</th>
                    <th className="px-6 py-3 font-semibold bg-gray-50">Statut</th>
                    <th className="px-6 py-3 font-semibold bg-gray-50">Date</th>
                    <th className="px-6 py-3 font-semibold text-right bg-gray-50">Total</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                    {userOrders.map((o) => (
                    <tr key={o._id} className="transition hover:bg-gray-50">
                        <td className="px-6 py-4 font-mono text-xs text-gray-600">#{o.orderNumber}</td>
                        <td className="px-6 py-4 font-medium text-gray-800">{o.merchantId?.name || "—"}</td>
                        <td className="px-6 py-4">
                            <span className={`px-2 py-1 rounded text-xs font-bold border ${getOrderStatusClasses(o.status)}`}>
                                {o.status}
                            </span>
                        </td>
                        <td className="px-6 py-4 text-gray-500">
                            {new Date(o.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 font-bold text-right text-gray-900">{o.total} <span className="text-xs font-normal text-gray-400">MRU</span></td>
                    </tr>
                    ))}

                    {userOrders.length === 0 && (
                    <tr>
                        <td colSpan="5" className="px-6 py-12 text-center text-gray-400">
                        Aucun historique disponible pour cet utilisateur.
                        </td>
                    </tr>
                    )}
                </tbody>
                </table>
            </div>
            
            <div className="flex justify-end p-4 border-t border-gray-100 bg-gray-50">
                <button
                    onClick={() => setOrdersOpen(false)}
                    className="px-5 py-2 text-sm font-bold text-gray-700 transition bg-white border border-gray-200 rounded-xl hover:bg-gray-100"
                >
                    Fermer
                </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={confirmOpen}
        title="Confirmation requise"
        description="Voulez-vous vraiment modifier le statut de blocage de cet utilisateur ?"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleToggleBlock}
      />

      <ConfirmModal
        open={confirmDeleteAll}
        title="Zone de Danger : Suppression Massive"
        description="ATTENTION : Tous les utilisateurs (sauf l'admin) seront définitivement supprimés. Cette action est irréversible."
        onCancel={() => setConfirmDeleteAll(false)}
        onConfirm={handleDeleteAllUsers}
      />
    </div>
  );
}
